import React, { useState, useEffect } from 'react';
import { useMyContext } from '../../../context/MyContext';
import NavBar from '../../../component/b2b/navBar/b2bNavBar';
import Sidebar from '../../../component/b2b/sideBar/b2bSideBar';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import axios from 'axios';
import { toast } from 'react-toastify';
import '../../../css/custom-scrollbar.css';
import { b2bCreateWarehouse, Base_Url, getpincode, fetchUser } from '../../../config/config';
import { useNavigate } from 'react-router-dom';

// Icons
const WarehouseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
    <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
    <polyline points="9,22 9,12 15,12 15,22" />
  </svg>
);

const HeavyIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-7 h-7">
    <rect x="2" y="7" width="20" height="14" rx="2" />
    <path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2" />
    <line x1="12" y1="12" x2="12" y2="16" />
    <line x1="10" y1="14" x2="14" y2="14" />
  </svg>
);

const LiteIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-7 h-7">
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
  </svg>
);

const DenseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-7 h-7">
    <rect x="3" y="3" width="7" height="7" />
    <rect x="14" y="3" width="7" height="7" />
    <rect x="14" y="14" width="7" height="7" />
    <rect x="3" y="14" width="7" height="7" />
  </svg>
);

const WAREHOUSE_TYPES = [
  {
    id: 'lite',
    label: 'Lite',
    sublabel: '',
    description: '',
    color: 'emerald',
    icon: <LiteIcon />,
    gradient: 'from-emerald-50 to-green-50',
    border: 'border-emerald-400',
    badge: 'bg-emerald-100 text-emerald-700',
    ring: 'ring-emerald-400',
    dot: 'bg-emerald-400',
  },
  {
    id: 'heavy',
    label: 'Heavy',
    sublabel: '',
    description: 'Bulk shipments & heavy cargo',
    color: 'orange',
    icon: <HeavyIcon />,
    gradient: 'from-orange-50 to-amber-50',
    border: 'border-orange-400',
    badge: 'bg-orange-100 text-orange-700',
    ring: 'ring-orange-400',
    dot: 'bg-orange-400',
  },
  {
    id: 'dense',
    label: 'Dense',
    sublabel: '',
    description: 'High-density stacked storage',
    color: 'violet',
    icon: <DenseIcon />,
    gradient: 'from-violet-50 to-purple-50',
    border: 'border-violet-400',
    badge: 'bg-violet-100 text-violet-700',
    ring: 'ring-violet-400',
    dot: 'bg-violet-400',
  },
];

const schema = z.object({
  facilityName: z.string()
    .min(1, "Facility name is required")
    .regex(/^[A-Za-z0-9\s]+$/, "Facility name must only contain letters, numbers, and spaces"),
  contactPerson: z.string()
    .min(1, "Contact Person is required")
    .regex(/^[A-Za-z0-9\s-]+$/, "Contact Person must only contain letters, numbers, spaces, and hyphens"),
  phone: z.string()
    .length(10, "Phone number must be exactly 10 digits")
    .regex(/^\d+$/, "Phone number must be numeric"),
  pincode: z.string()
    .min(6, "Pincode should be exactly 6 characters")
    .max(6, "Pincode should be exactly 6 characters")
    .regex(/^\d+$/, "Pincode must be numeric"),
  city: z.string().min(1, "City is required").regex(/^[a-zA-Z\s]+$/, "City must only contain alphabetic characters"),
  state: z.string().min(1, "State is required").regex(/^[a-zA-Z\s]+$/, "State must only contain alphabetic characters"),
  email: z.string().optional(),
  address: z.string()
    .min(1, "Address is required")
    .regex(/^[^#\\%&;]*$/, "Address contains invalid characters (# \\ % & ; not allowed)"),
  returnAddress: z.string().optional(),
  returnPincode: z.string().optional(),
});

// Reusable field component — compact
const Field = ({ label, id, error, isDarkMode, children }) => (
  <div className="flex flex-col gap-0.5">
    <label htmlFor={id} className={`text-[10px] font-semibold tracking-wide uppercase ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
      {label}
    </label>
    {children}
    {error && (
      <p className="text-red-500 text-[10px] leading-tight flex items-center gap-1">
        <span>⚠</span> {error.message}
      </p>
    )}
  </div>
);

const inputClass = (hasError, isDarkMode) =>
  `w-full px-2.5 py-1.5 text-xs rounded-md border transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent
  ${hasError ? 'border-red-400 bg-red-50' : isDarkMode ? 'border-gray-600 bg-gray-800 text-white placeholder-gray-500' : 'border-gray-200 bg-gray-50 text-gray-800 placeholder-gray-400'}`;

const AddWarehouse = () => {
  const { isToggleSidebar, isDarkMode } = useMyContext();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [retailerId, setRetailerId] = useState('');
  const [retailerIdData, setRetailerIdData] = useState([]);
  const [selectedWarehouseType, setSelectedWarehouseType] = useState('lite');
  const role = localStorage.getItem("role");
  const navigate = useNavigate();

  const { register, handleSubmit, formState: { errors }, setValue } = useForm({
    resolver: zodResolver(schema),
  });

  useEffect(() => { fetchRetailer(); }, []);

  const handleInputChange = (event) => {
    const { value, id } = event.target;
    if (['pincode', 'phone', 'return-pincode'].includes(id)) {
      const filtered = value.replace(/[^0-9]/g, '');
      event.target.value = filtered;
      if (id === 'pincode' && filtered.length === 6) fetchPincodeData(filtered);
      if (id === 'return-pincode' && filtered.length === 6) fetchReturnPincodeData(filtered);
    }
    if (['city', 'state', 'returnState', 'returnCity', 'country'].includes(id)) {
      event.target.value = value.replace(/[^a-zA-Z\s]/g, '');
    }
  };

  const fetchPincodeData = async (pincode) => {
    const token = localStorage.getItem('token');
    const cus_id = localStorage.getItem('cus_id');
    try {
      const response = await fetch(`${Base_Url}${getpincode}?pincode=${pincode}`, {
        headers: { token, cus_id, 'Content-Type': 'application/json' },
      });
      const data = await response.json();
      if (data?.status === "SUCCESS") {
        let { city, state } = data.data[0];
        setValue("city", city.charAt(0).toUpperCase() + city.slice(1).toLowerCase());
        setValue("state", state.charAt(0).toUpperCase() + state.slice(1).toLowerCase());
      } else if (data?.status === "FAIL") {
        toast.error("Invalid pincode, please try another.");
      }
    } catch { toast.error("Error fetching pincode data"); }
  };

  const fetchReturnPincodeData = async (returnPincode) => {
    const token = localStorage.getItem('token');
    const cus_id = localStorage.getItem('cus_id');
    try {
      const response = await fetch(`${Base_Url}${getpincode}?pincode=${returnPincode}`, {
        headers: { token, cus_id, 'Content-Type': 'application/json' },
      });
      const data = await response.json();
      if (data?.status === "SUCCESS") {
        let { city, state } = data.data[0];
        setValue("returnCity", city.charAt(0).toUpperCase() + city.slice(1).toLowerCase());
        setValue("returnState", state.charAt(0).toUpperCase() + state.slice(1).toLowerCase());
      } else if (data?.status === "FAIL") {
        toast.error("Invalid return pincode.");
      }
    } catch { toast.error("Error fetching return pincode data"); }
  };

  const fetchRetailer = async () => {
    const token = localStorage.getItem('token');
    const cusid = localStorage.getItem('cus_id');
    try {
      const response = await fetch(Base_Url + fetchUser, {
        method: 'GET',
        headers: { token, cus_id: cusid, 'Content-Type': 'application/json' },
      });
      const data = await response.json();
      setRetailerIdData(data.data.map(r => ({ value: r.id, label: r.name })));
    } catch (error) { console.error('Error fetching retailers:', error); }
  };

  const onSubmit = async (data, event) => {
    event.preventDefault();
    const token = localStorage.getItem('token');
    const adminId = localStorage.getItem('cus_id');
    setIsSubmitting(true);

    const payload = {
      pin_code: data.pincode,
      city: data.city,
      state: data.state,
      country: "India",
      cft_type: selectedWarehouseType,
      address_details: {
        address: data.address,
        contact_person: data.contactPerson,
        phone_number: data.phone,
      },
      name: data.facilityName,
      ret_address: {
        pin: data.returnPincode,
        address: data.returnAddress,
      },
    };

    // console.log(payload); return false;


    try {
      const response = await axios.post(Base_Url + b2bCreateWarehouse, payload, {
        headers: { token, cus_id: adminId, 'Content-Type': 'application/json' },
      });

      if (response?.data?.status === "SUCCESS") {
        toast.success(response.data.message);
        setTimeout(() => navigate("/b2b/warehouse/all"), 1000);
      } else {
        const m = response.data.messages || {};
        toast.error(m.pin_code || m.country || m.name || m["ret_address.pin"] || m["ret_address.address"] || m["address_details.phone_number"] || "Something went wrong.");
      }
    } catch (error) {
      const m = error?.response?.data?.messages || {};
      toast.error(m.pin_code || m.country || m.name || m["ret_address.pin"] || m["ret_address.address"] || m["address_details.phone_number"] || "Something went wrong.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const activeType = WAREHOUSE_TYPES.find(t => t.id === selectedWarehouseType);

  return (
    <div className={`h-screen flex flex-col overflow-hidden ${isDarkMode ? 'dark:bg-darkmodeColor' : 'bg-gray-50'}`}>
      <div className="z-10"><NavBar /></div>

      <div className="flex flex-1 overflow-hidden">
        {isToggleSidebar && (
          <div
            className={`fixed z-20 h-full w-64 md:relative duration-300 bg-gray-200 dark:bg-darkmodeColor dark:text-white md:bg-transparent mt-4 md:w-64 md:z-20`}
            style={window.innerWidth < 768 ? { top: "113px", height: "calc(100vh - 115px)", overflowY: 'auto' } : {}}
          >
            <Sidebar />
          </div>
        )}

        {/* overflow-y-auto kept as safety net for very short viewports, but layout below is sized to fit without needing it */}
        <div className={`flex-1 overflow-y-auto bg-gray-100 custom-scrollbar px-2 md:px-3 py-2 dark:bg-darkmodeColor`}>
          <div className="h-full flex flex-col max-w-6xl mx-auto w-full">

            {/* ── Page Header ── */}
            <div className="flex items-center gap-2 mb-2 flex-shrink-0">
              {/* <button
                onClick={() => navigate('/dashboard')}
                className={`p-1.5 rounded-md text-xs transition ${isDarkMode ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-200 text-gray-500'}`}
              >
                ← Back
              </button> */}
              <div className="flex items-center gap-2">
                <div className="p-1.5 bg-purple-100 rounded-md text-purple-600"><WarehouseIcon /></div>
                <div>
                  <h1 className={`text-sm font-bold leading-tight ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                    Add New Pickup Location
                  </h1>
                  <p className={`text-[10px] leading-tight ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Configure your warehouse details and type
                  </p>
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit(onSubmit, () => toast.error("Please fix the errors in the form."))}
              className="flex flex-col gap-2  min-h-0">

              {/* ── Warehouse Type Selector ── */}
              <div className={`rounded-lg border px-3 py-2 flex flex-col sm:flex-row sm:items-center gap-2 flex-shrink-0 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} shadow-sm`}>
                <div className="flex-shrink-0">
                  <p className={`text-xs font-semibold leading-tight ${isDarkMode ? 'text-white' : 'text-gray-700'}`}>
                    Warehouse Type
                  </p>
                  <p className={`text-[10px] leading-tight ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`}>
                    Select storage capacity
                  </p>
                </div>

                {/* Divider */}
                <div className={`hidden sm:block w-px self-stretch ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />

                {/* Pills — fill remaining space */}
                <div className="flex flex-1 gap-1.5 flex-wrap">
                  {WAREHOUSE_TYPES.map((type) => {
                    const isActive = selectedWarehouseType === type.id;
                    return (
                      <button
                        key={type.id}
                        type="button"
                        onClick={() => setSelectedWarehouseType(type.id)}
                        className={`
                          flex items-center gap-1.5 px-2.5 py-1 rounded-md border text-xs font-medium transition-all duration-150 cursor-pointer
                          ${isActive
                            ? `${type.border} ${type.badge} border-2`
                            : isDarkMode
                              ? 'border-gray-600 bg-gray-700 text-gray-300 hover:border-gray-500 hover:bg-gray-600'
                              : 'border-gray-200 bg-gray-50 text-gray-500 hover:bg-gray-100 hover:border-gray-300'
                          }
                        `}
                      >
                        {isActive && <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${type.dot}`} />}
                        <span>{type.label}</span>
                        <span className={`text-[10px] font-normal ${isActive ? 'opacity-80' : 'opacity-50'}`}>{type.sublabel}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* ── Facility Details ── */}
              <div className={`rounded-lg border p-3 flex-shrink-0 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} shadow-sm`}>
                <p className={`text-xs font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-700'}`}>
                  Facility Details
                </p>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">

                  {role === 'admin' && (
                    <Field label="Select Retailer" id="retailer" isDarkMode={isDarkMode}>
                      <div className="relative">
                        <select
                          id="retailer"
                          className={inputClass(false, isDarkMode)}
                          onChange={(e) => setRetailerId(e.target.value)}
                        >
                          <option value="">Select Retailer</option>
                          {retailerIdData.map(item => (
                            <option key={item.value} value={item.value}>{item.label}</option>
                          ))}
                        </select>
                      </div>
                    </Field>
                  )}

                  <Field label="Facility Name" id="facility-name" error={errors.facilityName} isDarkMode={isDarkMode}>
                    <input id="facility-name" type="text" placeholder="e.g. North Delhi Hub"
                      className={inputClass(errors.facilityName, isDarkMode)}
                      {...register('facilityName')} onChange={handleInputChange} />
                  </Field>

                  <Field label="Contact Person" id="contact-person" error={errors.contactPerson} isDarkMode={isDarkMode}>
                    <input id="contact-person" type="text" placeholder="Full name"
                      className={inputClass(errors.contactPerson, isDarkMode)}
                      {...register('contactPerson')} onChange={handleInputChange} />
                  </Field>

                  <Field label="Phone Number" id="phone" error={errors.phone} isDarkMode={isDarkMode}>
                    <input id="phone" type="tel" maxLength="10" placeholder="10-digit mobile number"
                      className={inputClass(errors.phone, isDarkMode)}
                      {...register('phone')} onChange={handleInputChange} />
                  </Field>

                  <Field label="Pincode" id="pincode" error={errors.pincode} isDarkMode={isDarkMode}>
                    <input id="pincode" type="text" maxLength="6" placeholder="6-digit pincode"
                      className={inputClass(errors.pincode, isDarkMode)}
                      {...register('pincode')} onChange={handleInputChange} />
                  </Field>

                  <Field label="City" id="city" error={errors.city} isDarkMode={isDarkMode}>
                    <input id="city" type="text" placeholder="Auto-filled"
                      className={`${inputClass(errors.city, isDarkMode)} disabled:opacity-60 cursor-not-allowed`}
                      {...register('city')} disabled onChange={handleInputChange} />
                  </Field>

                  <Field label="State" id="state" error={errors.state} isDarkMode={isDarkMode}>
                    <input id="state" type="text" placeholder="Auto-filled"
                      className={`${inputClass(errors.state, isDarkMode)} disabled:opacity-60 cursor-not-allowed`}
                      {...register('state')} disabled onChange={handleInputChange} />
                  </Field>

                  <Field label="Country" id="country" isDarkMode={isDarkMode}>
                    <input id="country" type="text" value="India" disabled
                      className={`${inputClass(false, isDarkMode)} disabled:opacity-60 cursor-not-allowed`} />
                  </Field>

                  <Field label="Address" id="address" error={errors.address} isDarkMode={isDarkMode}>
                    <input id="address" type="text" placeholder="Building, street, area"
                      className={inputClass(errors.address, isDarkMode)}
                      {...register('address')} />
                  </Field>

                </div>
              </div>

              {/* ── Return Address ── */}
              <div className={`rounded-lg border p-3 flex-shrink-0 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} shadow-sm`}>
                <div className="flex items-baseline gap-1.5 mb-2">
                  <p className={`text-xs font-semibold ${isDarkMode ? 'text-white' : 'text-gray-700'}`}>
                    Return Address
                  </p>
                  <span className={`text-[10px] font-normal ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                    (optional — leave blank to use the same address for returns)
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                  <Field label="Return Pincode" id="return-pincode" isDarkMode={isDarkMode}>
                    <input id="return-pincode" type="text" maxLength="6" placeholder="6-digit return pincode"
                      className={inputClass(false, isDarkMode)}
                      {...register('returnPincode')} onChange={handleInputChange} />
                  </Field>

                  <Field label="Return Address" id="returnAddress" isDarkMode={isDarkMode}>
                    <input id="returnAddress" type="text" placeholder="Return location address"
                      className={inputClass(false, isDarkMode)}
                      {...register('returnAddress')} />
                  </Field>
                </div>
              </div>

              {/* ── Submit ── */}
              <div className="flex items-center justify-end gap-2  mt-2 pb-1">
                <button
                  type="button"
                  onClick={() => navigate('/b2b/warehouse/all')}
                  className={`px-4 py-1.5 rounded-md text-xs font-medium border transition ${isDarkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-600 hover:bg-gray-100'}`}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`flex-1 md:flex-none md:min-w-[180px] flex items-center justify-center gap-1.5 px-5 py-1.5 rounded-md text-xs font-semibold text-white transition-all duration-200
                    ${isSubmitting
                      ? 'bg-purple-400 cursor-not-allowed'
                      : 'bg-purple-600 hover:bg-purple-700 active:scale-95 shadow-md hover:shadow-purple-200'
                    }`}
                >
                  {isSubmitting ? (
                    <>
                      <svg className="animate-spin w-3.5 h-3.5" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                      </svg>
                      Creating Warehouse...
                    </>
                  ) : (
                    <>
                      <WarehouseIcon />
                      Create {activeType?.label} Warehouse
                    </>
                  )}
                </button>
              </div>

            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddWarehouse;